// Phase 9 — full-bleed two-column hero login page.
// Left: dark cinematic hero with brand wordmark, hero copy, scan-line and
// radar-pulse CSS animations, and a live /api/health system-status strip.
// Right: focused glass form card. Mobile: stacked.
import { useEffect, useState } from "react";
import { Eye, EyeOff, ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAuth } from "@/auth/AuthContext";
import { health as healthApi } from "@/api/endpoints";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/formatApiError";

const APP_VERSION = "v0.1.0";

export default function LoginPage() {
    const navigate = useNavigate();
    const { login, isAuthenticated, mustChangePassword, bootstrapping } =
        useAuth();

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [systemStatus, setSystemStatus] = useState(null);

    useEffect(() => {
        if (bootstrapping) return;
        if (isAuthenticated) {
            navigate(mustChangePassword ? "/account/change-password" : "/runs", {
                replace: true,
            });
        }
    }, [bootstrapping, isAuthenticated, mustChangePassword, navigate]);

    useEffect(() => {
        let cancelled = false;
        healthApi
            .get()
            .then((d) => {
                if (cancelled) return;
                setSystemStatus({
                    jsbsim: d?.jsbsim?.installed
                        ? `JSBSim ${d.jsbsim.version || ""}`.trim()
                        : "JSBSim",
                    kinematic: "Kinematic Engine",
                    mongo:
                        d?.selfcheck?.mongo?.connected === false
                            ? "Mongo · down"
                            : "Mongo",
                    online: true,
                });
            })
            .catch(() => {
                if (!cancelled)
                    setSystemStatus({
                        jsbsim: "JSBSim",
                        kinematic: "Kinematic Engine",
                        mongo: "System",
                        online: false,
                    });
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!username.trim() || !password) {
            setErrorMsg("Username and password are required.");
            return;
        }
        setSubmitting(true);
        setErrorMsg(null);
        try {
            const user = await login(username.trim(), password);
            toast.success(`Signed in as ${user.username}`);
            navigate(
                user.must_change_password ? "/account/change-password" : "/runs",
                { replace: true },
            );
        } catch (err) {
            const status = err?.response?.status;
            const detail = formatApiError(err, "Sign-in failed.");
            if (status === 429) {
                const retry =
                    err?.response?.headers?.["retry-after"] || "900";
                setErrorMsg(
                    `Too many failed attempts. Try again in ${retry} seconds.`,
                );
            } else if (status === 401) {
                setErrorMsg("Invalid username or password.");
            } else {
                setErrorMsg(detail);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const STYLES = `
        @keyframes uavsimnet-scan-line {
            0%   { transform: translateY(-4%); opacity: 0; }
            8%   { opacity: 1; }
            92%  { opacity: 1; }
            100% { transform: translateY(104%); opacity: 0; }
        }
        @keyframes uavsimnet-radar-pulse {
            0%   { transform: scale(0.2); opacity: 0.0; }
            10%  { opacity: 0.08; }
            100% { transform: scale(1.0); opacity: 0; }
        }
        @keyframes uavsimnet-fade-in-up {
            from { opacity: 0; transform: translateY(8px); }
            to   { opacity: 1; transform: translateY(0);   }
        }
        .uavsimnet-fade-in-up { animation: uavsimnet-fade-in-up 250ms ease-out both; }
    `;

    return (
        <div
            className="min-h-screen w-full bg-[var(--ux-bg-0)] text-[var(--ux-text-1)] flex flex-col lg:flex-row"
            data-testid="login-page"
        >
            <style>{STYLES}</style>

            {/* ============== LEFT — HERO ============== */}
            <section
                className="relative flex-none lg:flex-[3] h-[220px] lg:h-auto overflow-hidden"
                aria-hidden="false"
                data-testid="login-hero"
            >
                {/* Hero image */}
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: "url('/login-hero.jpg')",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                {/* Dark gradient for legibility */}
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(135deg, rgba(11,15,20,0.72) 0%, rgba(11,15,20,0.92) 100%)",
                    }}
                />
                {/* Faint grid for depth */}
                <div
                    className="absolute inset-0 opacity-[0.05] pointer-events-none"
                    style={{
                        backgroundImage:
                            "linear-gradient(var(--ux-text-1) 1px, transparent 1px), linear-gradient(90deg, var(--ux-text-1) 1px, transparent 1px)",
                        backgroundSize: "44px 44px",
                    }}
                />

                {/* Radar pulses (3 staggered concentric circles) */}
                <div
                    aria-hidden="true"
                    className="hidden lg:block pointer-events-none absolute"
                    style={{ left: "5%", bottom: "8%", width: 600, height: 600 }}
                    data-testid="login-radar"
                >
                    {[0, 1.6, 3.2].map((delay, i) => (
                        <span
                            key={i}
                            style={{
                                position: "absolute",
                                left: "50%",
                                top: "50%",
                                width: 600,
                                height: 600,
                                marginLeft: -300,
                                marginTop: -300,
                                borderRadius: "50%",
                                border: "1px solid var(--ux-accent)",
                                animation:
                                    "uavsimnet-radar-pulse 5s ease-out infinite",
                                animationDelay: `${delay}s`,
                                opacity: 0,
                                willChange: "transform, opacity",
                            }}
                        />
                    ))}
                </div>

                {/* Scan line */}
                <div
                    aria-hidden="true"
                    className="hidden lg:block pointer-events-none absolute inset-0"
                >
                    <span
                        style={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            top: 0,
                            height: 1,
                            background:
                                "linear-gradient(90deg, transparent 0%, var(--ux-accent) 50%, transparent 100%)",
                            opacity: 0.04,
                            animation:
                                "uavsimnet-scan-line 6s linear infinite",
                            willChange: "transform, opacity",
                        }}
                    />
                </div>

                {/* Top-left brand */}
                <div className="relative z-10 px-6 lg:px-12 pt-6 lg:pt-10 flex items-center gap-3">
                    <Logo variant="wordmark" size="md" />
                    <span
                        className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border border-[var(--ux-border-2)] text-[var(--ux-text-3)] font-mono"
                        data-testid="login-version-chip"
                    >
                        {APP_VERSION}
                    </span>
                </div>

                {/* Hero copy — centred vertically on desktop */}
                <div className="relative z-10 px-6 lg:px-12 mt-6 lg:mt-0 lg:absolute lg:inset-0 lg:flex lg:flex-col lg:justify-center lg:max-w-[640px]">
                    <div className="uavsimnet-fade-in-up" style={{ animationDelay: "60ms" }}>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--ux-text-3)]">
                            Counter-UAS · Mission Analysis
                        </div>
                    </div>
                    <h1
                        className="uavsimnet-fade-in-up mt-2 lg:mt-3 font-extrabold tracking-tight text-[var(--ux-text-1)] leading-[1.05] text-[28px] lg:text-[42px]"
                        style={{ animationDelay: "140ms" }}
                        data-testid="login-hero-title"
                    >
                        Welcome back, Operator.<br className="hidden lg:inline" />
                        <span className="text-[var(--ux-accent)]"> The intercept is ready.</span>
                    </h1>
                    <p
                        className="uavsimnet-fade-in-up mt-3 max-w-md text-[14px] lg:text-[15px] text-[var(--ux-text-2)] leading-relaxed"
                        style={{ animationDelay: "220ms" }}
                    >
                        Your engagements, your scenarios, your evidence —
                        signed in and waiting.
                    </p>
                </div>

                {/* System-status footer strip */}
                <div
                    className="relative z-10 hidden lg:flex absolute bottom-6 left-12 items-center gap-4 text-[11px] font-mono text-[var(--ux-text-2)]"
                    style={{ position: "absolute" }}
                    data-testid="login-system-status"
                >
                    {[
                        systemStatus?.jsbsim || "JSBSim",
                        systemStatus?.kinematic || "Kinematic Engine",
                        systemStatus?.mongo || "Mongo",
                    ].map((label, i) => (
                        <span
                            key={i}
                            className="inline-flex items-center gap-1.5"
                        >
                            <span
                                className="inline-block w-1.5 h-1.5 rounded-full"
                                style={{
                                    background: systemStatus?.online === false
                                        ? "var(--ux-warning)"
                                        : "var(--ux-success)",
                                }}
                            />
                            {label}
                        </span>
                    ))}
                </div>
            </section>

            {/* ============== RIGHT — FORM ============== */}
            <section
                className="relative flex-1 lg:flex-[2] flex items-center justify-center px-6 py-10 bg-[var(--ux-bg-1)] border-t lg:border-t-0 lg:border-l-2 border-[var(--ux-border-1)]"
                data-testid="login-form-section"
            >
                <div
                    className="w-full max-w-[360px]"
                    data-testid="login-card"
                >
                    <div className="flex justify-center">
                        <Logo variant="glyph" size="lg" />
                    </div>
                    <h2 className="mt-5 text-center text-[22px] font-semibold text-[var(--ux-text-1)]">
                        Sign in to UAVSimNet
                    </h2>
                    <p className="mt-1 text-center text-[13px] text-[var(--ux-text-2)]">
                        Enter your operator credentials
                    </p>

                    <form onSubmit={onSubmit} className="mt-6 space-y-4">
                        <div
                            className="space-y-1.5 uavsimnet-fade-in-up"
                            style={{ animationDelay: "60ms" }}
                        >
                            <Label
                                htmlFor="login-username"
                                className="text-xs uppercase tracking-wider text-[var(--ux-text-3)]"
                            >
                                Username
                            </Label>
                            <Input
                                id="login-username"
                                data-testid="login-username-input"
                                type="text"
                                autoComplete="username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="admin"
                                className="bg-[var(--ux-bg-2)] border-[var(--ux-border-1)] text-[var(--ux-text-1)] focus-visible:ring-[var(--ux-accent)]"
                            />
                        </div>

                        <div
                            className="space-y-1.5 uavsimnet-fade-in-up"
                            style={{ animationDelay: "140ms" }}
                        >
                            <Label
                                htmlFor="login-password"
                                className="text-xs uppercase tracking-wider text-[var(--ux-text-3)]"
                            >
                                Password
                            </Label>
                            <div className="relative">
                                <Input
                                    id="login-password"
                                    data-testid="login-password-input"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••••"
                                    className="bg-[var(--ux-bg-2)] border-[var(--ux-border-1)] text-[var(--ux-text-1)] focus-visible:ring-[var(--ux-accent)] pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    aria-label={
                                        showPassword
                                            ? "Hide password"
                                            : "Show password"
                                    }
                                    data-testid="login-password-toggle"
                                    className="absolute inset-y-0 right-2 flex items-center text-[var(--ux-text-3)] hover:text-[var(--ux-text-1)] transition-colors"
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {errorMsg && (
                            <div
                                className="flex items-start gap-2 rounded-sm border border-[var(--ux-danger)]/40 bg-[var(--ux-danger-soft)] px-3 py-2 text-sm text-[var(--ux-danger)]"
                                data-testid="login-error"
                                role="alert"
                            >
                                <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                                <span>{errorMsg}</span>
                            </div>
                        )}

                        <Button
                            type="submit"
                            data-testid="login-submit-button"
                            disabled={submitting}
                            className="uavsimnet-fade-in-up w-full bg-[var(--ux-accent)] text-[var(--ux-bg-0)] hover:bg-[var(--ux-accent)]/90 disabled:opacity-60"
                            style={{ animationDelay: "220ms" }}
                        >
                            {submitting ? "Signing in…" : "Sign in"}
                        </Button>

                        <p className="text-[11px] text-[var(--ux-text-3)] leading-relaxed pt-1 text-center">
                            Forgot password? Contact your administrator.
                        </p>
                    </form>

                    {/* Public documentation affordance. The /guide URL
                        redirects to the new public docs landing page at
                        /docs (four capability-level chapters). */}
                    <div className="mt-5 pt-4 border-t border-[var(--ux-border-1)] text-center">
                        <a
                            href="/docs"
                            data-testid="login-docs-link"
                            className="inline-flex items-center gap-1.5 text-[11px] font-mono text-[var(--ux-text-3)] hover:text-[var(--ux-accent)] transition-colors"
                        >
                            <span>Public documentation</span>
                            <span aria-hidden>→</span>
                        </a>
                    </div>
                </div>

                <span
                    className="absolute bottom-3 right-4 text-[10px] font-mono text-[var(--ux-text-3)]"
                    data-testid="login-footer-version"
                >
                    {APP_VERSION}
                </span>
            </section>
        </div>
    );
}
